import React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

type RadioGroupWidth = "sm" | "md" | "lg" | "full";

interface RadioGroupRootProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

interface RadioGroupItemProps {
  value: string;
  label?: string;
  sublabel?: string;
  width?: RadioGroupWidth;
  transform?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * RadioGroup component for single-selection scenarios.
 * Built on Radix UI RadioGroup with button-styled items.
 *
 * Provides proper keyboard navigation (arrow keys) and accessibility.
 * Use for: root note selection, scale selection, etc.
 *
 * Example:
 * ```tsx
 * <RadioGroup value={selectedNote} onValueChange={setSelectedNote}>
 *   <RadioGroup.Item value="C" label="C" width="sm" />
 *   <RadioGroup.Item value="D" label="D" width="sm" />
 * </RadioGroup>
 * ```
 */
const RadioGroupRoot = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupRootProps
>(({ value, onValueChange, children, className }, forwardedRef) => {
  return (
    <RadioGroupPrimitive.Root
      ref={forwardedRef}
      value={value}
      onValueChange={onValueChange}
      className={className}
    >
      {children}
    </RadioGroupPrimitive.Root>
  );
});

RadioGroupRoot.displayName = "RadioGroup";

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(
  (
    { value, label, sublabel, width = "sm", transform, className, children },
    forwardedRef
  ) => {
    const getWidthClass = () => {
      switch (width) {
        case "sm":
          return "w-16";
        case "md":
          return "w-24";
        case "lg":
          return "w-48";
        case "full":
          return "w-full";
        default:
          return "w-16";
      }
    };

    const getTransformClass = () => {
      if (transform) {
        return `transform ${transform}`;
      }
      return "";
    };

    return (
      <RadioGroupPrimitive.Item
        ref={forwardedRef}
        value={value}
        asChild
      >
        <button
          type="button"
          className={`${getWidthClass()} ${getTransformClass()} h-16 flex flex-col items-center justify-center rounded font-mono text-lg border-4 transition-colors data-[state=checked]:border-green-500 data-[state=unchecked]:border-gray-800 ${className || ""}`}
        >
          {children ? (
            children
          ) : (
            <>
              {sublabel && <span className="text-xs text-gray-400">{sublabel}</span>}
              {label && <span className="text-xl">{label}</span>}
            </>
          )}
        </button>
      </RadioGroupPrimitive.Item>
    );
  }
);

RadioGroupItem.displayName = "RadioGroupItem";

export const RadioGroup = Object.assign(RadioGroupRoot, {
  Item: RadioGroupItem,
});

export default RadioGroup;
